import React, { useState, useEffect, useMemo } from 'react';
import { onAuthStateChanged, signOut } from "firebase/auth";
import { collection, onSnapshot, addDoc, query, getDocs, doc, updateDoc, setDoc } from "firebase/firestore";
import styled, { ThemeProvider } from 'styled-components';
import { auth, db } from './config/firebase';
import { theme } from './config/theme';
import { exportToGoogleSheets } from './utils/googleApiHelpers';
import { ROLES, ROLES_ELEGIBLES_SIN_ASIGNAR, ROLES_OPERATIVOS_ADMIN } from './constants/roles';
import { registerComercialLogin } from './services/comercialesService';
import { registrarAccesoUsuario } from './services/rolesUsuariosService';
import { parseCuitDigits } from './utils/formatters';
import { buildFiltroMisClientes, resolveAlcanceDashboard, scopeKey } from './utils/saldosScope';
import { resolveRolOperativo, veUsuarios } from './utils/rolesUsuario';
import { isAxonEmail } from './utils/emailDomain';

// Componentes
import ClientManagementModal from './components/ClientManagementModal';
import LoginScreen from './components/LoginScreen';
import Header from './components/Header';
import DashboardTab from './components/DashboardTab';
import CaucionesTab from './components/CaucionesTab';
import CaucionFormModal from './components/CaucionFormModal';
import OperacionesTab from './components/OperacionesTab';
import InstruccionesTab from './components/InstruccionesTab';
import LicitacionesTab from './components/LicitacionesTab';
import SenebiTab from './components/SenebiTab';
import Notification from './components/Notification';
import SplashScreen from './components/common/SplashScreen';
import RoleSelectionModal from './components/RoleSelectionModal';
import AddEspecieModal from './components/AddEspecieModal';
import CierreDiarioModal from './components/CierreDiarioModal';
import CommentModal from './components/CommentModal';
import ControlSaldosTab from './components/ControlSaldosTab';
import ExportReminderModal from './components/ExportReminderModal';
import UpdateBanner from './components/UpdateBanner';
import ProductoresAdminModal from './components/ProductoresAdminModal';
import UsuariosAdminModal from './components/UsuariosAdminModal';
import ProductorView from './components/productor/ProductorView';
import ProductorInactivoScreen from './components/productor/ProductorInactivoScreen';
import MisClientesTab from './components/clientes/MisClientesTab';
import MisClientesToggle from './components/common/MisClientesToggle';

// Hooks
import useExportReminder from './hooks/useExportReminder';
import { useVersionCheck } from './hooks/useVersionCheck';
import { usePersistentTab } from './hooks/useTabBar';
import { MainContent, MainTabBar } from './components/common/MainTabs';
import { useProductorActual } from './hooks/useProductorActual';
import { useRolUsuario } from './hooks/useRolUsuario';
import { SaldosClientesProvider } from './hooks/useSaldosClientes';

// --- Styled Components (sin cambios) ---
const AppWrapper = styled.div`
  background-color: ${props => props.theme.colors.background};
  /* #root ya reserva los márgenes seguros del celular (ver GlobalStyles). */
  min-height: calc(100vh - env(safe-area-inset-top) - env(safe-area-inset-bottom));
`;

// `soloComercial` / `sinComercial`: pestañas que se muestran o se ocultan según el
// rol con el que se opera (SENEBI tiene los márgenes de la casa; las reglas de
// Firestore tampoco se lo dejan leer a un comercial).
const TABS = [
    { key: 'misClientes', label: 'Mis Clientes', soloComercial: true },
    { key: 'dashboard', label: 'Dashboard' },
    { key: 'cauciones', label: 'Cauciones/Pases' },
    { key: 'operaciones', label: 'Operaciones' },
    { key: 'instrucciones', label: 'Instrucciones' },
    { key: 'licitaciones', label: 'Licitaciones' },
    { key: 'senebi', label: 'SENEBI', sinComercial: true },
    { key: 'saldos', label: 'Control de Saldos' },
];
const TAB_KEYS = TABS.map(tab => tab.key);

// Pestañas donde el comercial elige "Mis clientes / Toda la casa".
const TABS_CON_FILTRO_MIS_CLIENTES = ['cauciones', 'operaciones', 'instrucciones', 'licitaciones', 'saldos'];

const VISTA_MIS_CLIENTES_KEY = 'planilla.vistaMisClientes';

const leerVistaMisClientes = () => {
    try {
        return window.localStorage.getItem(VISTA_MIS_CLIENTES_KEY) !== 'todos';
    } catch {
        return true;
    }
};

const CARTERA_VACIA = { clientes: [], comitentes: [], clientesSinComitente: [] };


export default function App() {
    const { updateAvailable, reloadNow } = useVersionCheck();
    const [activeTab, setActiveTab] = usePersistentTab('planilla.activeTab', 'dashboard', TAB_KEYS);
    const [caucionModal, setCaucionModal] = useState({ open: false, prefill: null });
    const [user, setUser] = useState(null);
    const [isLoading, setIsLoading] = useState(true);
    // Rol elegido en la sesión: el de transición (quien no tiene rol asignado) o el
    // que elige un admin para actuar como otro rol. El rol fijo sale de useRolUsuario.
    const [rolElegido, setRolElegido] = useState(null);
    const [vistaMisClientes, setVistaMisClientes] = useState(leerVistaMisClientes);
    const [isUsuariosModalOpen, setIsUsuariosModalOpen] = useState(false);
    const [isClientModalOpen, setIsClientModalOpen] = useState(false);
    const [isAddEspecieModalOpen, setIsAddEspecieModalOpen] = useState(false);
    const [clientes, setClientes] = useState([]);
    const [cuentasJefferies, setCuentasJefferies] = useState([]);
    const [especies, setEspecies] = useState([]);
    const [loginError, setLoginError] = useState('');
    const [showNotification, setShowNotification] = useState(false);
    const [notificationShownToday, setNotificationShownToday] = useState(false);
    const [isCierreModalOpen, setIsCierreModalOpen] = useState(false);
    const [isExportingToDrive, setIsExportingToDrive] = useState(false);
    const [isExecutingCierre, setIsExecutingCierre] = useState(false);
    const [commentModalData, setCommentModalData] = useState({ isOpen: false, title: '', content: '' });
    const [titulosA3, setTitulosA3] = useState([]);
    const [isProductoresModalOpen, setIsProductoresModalOpen] = useState(false);

    // Un usuario del padrón `productores` (sincronizado desde el Sistema de Gestión
    // Comercial) no elige rol: va directo a su planilla acotada a sus clientes.
    // Solo una cuenta @axoncapital puede ser personal interno. Una cuenta de otro
    // dominio (p. ej. el Gmail de un productor) entra únicamente como productor del
    // padrón; si no figura, se cierra la sesión sin mostrar nada de la planilla.
    const { productor, isLoading: isProductorLoading } = useProductorActual(user);
    const esCuentaAxon = isAxonEmail(user?.email);
    const isStaffSession = !!user && esCuentaAxon && !isProductorLoading && productor === null;
    const isCuentaNoHabilitada = !!user && !esCuentaAxon && !isProductorLoading && productor === null;

    // Rol del personal interno (ver docs/roles.md): dueño → Admin; rol asignado en
    // el panel Usuarios; comercial del SGC → Comercial; si no, elige Trader o
    // Middle Office (transición). Un admin puede actuar como otro rol.
    const { rolUsuario, comercial, isLoading: isRolLoading } = useRolUsuario(
        isStaffSession ? user.email : null,
    );
    const userRole = resolveRolOperativo({ rolUsuario, rolElegido });
    const esAdmin = !!rolUsuario?.esAdmin;
    // Admins y quien tiene delegación del dueño para asignar roles.
    const puedeVerUsuarios = veUsuarios(rolUsuario);

    // Un comercial ve el Dashboard de Saldos y "Mis Clientes" acotados a los
    // clientes que tiene asignados en el SGC, y en las planillas elige "Mis
    // clientes / Toda la casa". Es recorte de vista, no de permisos: sigue siendo
    // staff (salvo SENEBI). Si no figura en el padrón del SGC ve el dashboard general.
    const esComercial = userRole === ROLES.COMERCIAL;
    const alcanceDashboard = useMemo(
        () => resolveAlcanceDashboard({ esComercial, comercial, isLoading: isRolLoading }),
        [esComercial, comercial, isRolLoading],
    );
    const carteraComercial = comercial && comercial.active !== false ? comercial : CARTERA_VACIA;
    const comitentesComercialKey = scopeKey(carteraComercial.comitentes);
    const filtroMisClientes = useMemo(
        () => (esComercial && vistaMisClientes && user
            ? buildFiltroMisClientes({
                comitentes: comitentesComercialKey ? comitentesComercialKey.split('|') : [],
                userId: user.uid,
                userName: user.displayName || '',
            })
            : null),
        [esComercial, vistaMisClientes, user, comitentesComercialKey],
    );

    const tabsVisibles = useMemo(
        () => TABS.filter(tab => (esComercial ? !tab.sinComercial : !tab.soloComercial)),
        [esComercial],
    );
    const tabActiva = tabsVisibles.some(tab => tab.key === activeTab) ? activeTab : 'dashboard';

    // Hook para recordatorio de exportación
    const { 
        showReminder, 
        currentTime, 
        dismissReminder, 
        handleExportAction 
    } = useExportReminder();

    const handleViewComment = (title, content) => {
        setCommentModalData({ isOpen: true, title, content });
    };

    const handleCloseCommentModal = () => {
        setCommentModalData({ isOpen: false, title: '', content: '' });
    };

    const GOOGLE_API_KEY = process.env.REACT_APP_GOOGLE_API_KEY;
    const GOOGLE_CLIENT_ID = process.env.REACT_APP_GOOGLE_CLIENT_ID;
    const SHARED_DRIVE_ID = process.env.REACT_APP_SHARED_DRIVE_ID;

    // --- ✅ CAMBIO: Lógica de exportación actualizada ---
    const handleExportToDrive = async () => {
        setIsExportingToDrive(true);
        try {
            // 1. Obtener todos los datos
            const caucionesQuery = query(collection(db, "cauciones"));
            const pasesQuery = query(collection(db, "pases"));
            const operacionesQuery = query(collection(db, "operaciones"));
            const senebiQuery = query(collection(db, "senebiOperations"));
            const instruccionesQuery = query(collection(db, "instrucciones"));

            const [caucionesSnapshot, pasesSnapshot, operacionesSnapshot, senebiSnapshot, instruccionesSnapshot] = await Promise.all([
                getDocs(caucionesQuery),
                getDocs(pasesQuery),
                getDocs(operacionesQuery),
                getDocs(senebiQuery),
                getDocs(instruccionesQuery),
            ]);

            const todasLasCauciones = caucionesSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
            const todosLosPases = pasesSnapshot.docs
                .map(doc => ({ id: doc.id, ...doc.data() }))
                .filter(p => p.estado !== 'Eliminada');
            const todasLasOperaciones = operacionesSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
            const todasLasSenebi = senebiSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
            const todasLasInstrucciones = instruccionesSnapshot.docs
                .map(doc => ({ id: doc.id, ...doc.data() }))
                .filter(i => i.estado !== 'Eliminada');

            // 2. Calcular los indicadores
            const caucionesIndicadores = todasLasCauciones.reduce((acc, caucion) => {
                const monto = parseFloat(caucion.montoOperado) || 0;
                const tasa = parseFloat(caucion.tasa) || 0;
                if (caucion.operacion === 'Colocadora') {
                    acc.totalColocadora += monto;
                    acc.weightedSumColocadora += tasa * monto;
                } else if (caucion.operacion === 'Tomadora') {
                    acc.totalTomadora += monto;
                    acc.weightedSumTomadora += tasa * monto;
                }
                return acc;
            }, { totalColocadora: 0, totalTomadora: 0, weightedSumColocadora: 0, weightedSumTomadora: 0 });
            
            const tnaColocadora = caucionesIndicadores.totalColocadora > 0 ? (caucionesIndicadores.weightedSumColocadora / caucionesIndicadores.totalColocadora) : 0;
            const tnaTomadora = caucionesIndicadores.totalTomadora > 0 ? (caucionesIndicadores.weightedSumTomadora / caucionesIndicadores.totalTomadora) : 0;
            
            const caucionIndicatorsData = {
                totalColocadora: caucionesIndicadores.totalColocadora,
                totalTomadora: caucionesIndicadores.totalTomadora,
                tnaColocadora: tnaColocadora,
                tnaTomadora: tnaTomadora
            };

            const senebiFeeTotals = todasLasSenebi.reduce((acc, op) => {
                const fee = parseFloat(op.fee) || 0;
                if (fee && acc.hasOwnProperty(op.moneda)) {
                    acc[op.moneda] += fee;
                }
                return acc;
            }, { Pesos: 0, "USD Mep": 0, "USD CCL": 0 });


            if (todasLasCauciones.length === 0 && todosLosPases.length === 0 && todasLasOperaciones.length === 0 && todasLasSenebi.length === 0 && todasLasInstrucciones.length === 0) {
                alert("No hay datos en ninguna de las planillas para exportar.");
                setIsExportingToDrive(false);
                return;
            }

            // 3. Llamar a la función de exportación con los nuevos datos
            const sheetUrl = await exportToGoogleSheets(
                todasLasCauciones,
                todasLasOperaciones,
                todasLasSenebi,
                todasLasInstrucciones,
                caucionIndicatorsData,
                senebiFeeTotals,
                GOOGLE_API_KEY,
                GOOGLE_CLIENT_ID,
                SHARED_DRIVE_ID,
                todosLosPases
            );

            alert(`¡Éxito! La planilla de Operaciones, Cauciones, Pases, SENEBI e Instrucciones se ha guardado en Google Drive.`);
            window.open(sheetUrl, '_blank');

        } catch (error) {
            console.error("Error al exportar a Google Drive: ", error);
            alert("Ocurrió un error al guardar en Google Drive. Revisa la consola.");
        } finally {
            setIsExportingToDrive(false);
        }
    };

    // Función para manejar exportación desde el recordatorio
    const handleExportFromReminder = async () => {
        handleExportAction(); // Cerrar el modal del recordatorio
        await handleExportToDrive(); // Ejecutar la exportación
    };
    
    const handleExecuteCierre = async () => {
        // Primera confirmación: Advertencia sobre el proceso automático
        if (!window.confirm(
            "⚠️ ATENCIÓN: Este proceso debe ejecutarse ÚNICAMENTE si falló el proceso automático que corre a las 21:00 hs.\n\n" +
            "¿Confirma que el proceso automático falló y necesita ejecutar el cierre manualmente?"
        )) {
            return;
        }

        // Segunda confirmación: Confirmación final
        if (!window.confirm("¿Está seguro? Esta acción es irreversible y preparará la planilla para el día siguiente.")) {
            return;
        }
        setIsExecutingCierre(true);
        try {
            // FIXED: Usar URL directa de Cloud Run para evitar políticas organizacionales
            console.log('🚀 Ejecutando cierre con Cloud Run directo (sin CORS)');
            
            const idToken = await user.getIdToken();
            const response = await fetch('https://us-central1-planilla-operaciones-axon.cloudfunctions.net/runDailyClosePublicV2', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${idToken}`
                },
                body: JSON.stringify({})
            });

            if (!response.ok) {
                const errorText = await response.text();
                throw new Error(`HTTP ${response.status}: ${errorText}`);
            }

            const data = await response.json();
            
            if (data.alreadyClosed) {
                alert(`El cierre para ${data.dateKey} ya había sido ejecutado. No se realizaron cambios.`);
            } else {
                const c = data.counts || {};
                alert(
                    `Cierre diario (${data.dateKey}) completado:\n` +
                    `- ${c.operacionesEliminadas || 0} operaciones eliminadas.\n` +
                    `- ${c.caucionesEliminadas || 0} cauciones eliminadas.\n` +
                    `- ${c.senebiEliminadas || 0} operaciones de SENEBI eliminadas.\n` +
                    `- ${c.instruccionesEliminadas || 0} instrucciones eliminadas.\n` +
                    `- ${c.saldosEliminados || 0} saldos de control eliminados.\n` +
                    `- ${c.tomadorasArchivadas || 0} cauciones tomadoras archivadas.\n` +
                    `- ${c.precargasCreadas || 0} cauciones precargadas para mañana.`
                );
            }
        } catch (error) {
            console.error("Error durante el cierre diario (backend): ", error);
            const msg = error?.message || 'Ocurrió un error durante el cierre diario.';
            alert(msg + ' Revise la consola para más detalles.');
        } finally {
            setIsExecutingCierre(false);
            setIsCierreModalOpen(false);
        }
    };
    
    useEffect(() => {
        const link = document.createElement('link');
        link.href = "https://fonts.googleapis.com/css2?family=Poppins:wght@300;600&family=Raleway:wght@600;700&family=Roboto:wght@400;500;700&display=swap";
        link.rel = 'stylesheet';
        document.head.appendChild(link);
    }, []);

    useEffect(() => {
        const checkTimeForNotification = () => {
            const now = new Date();
            const day = now.getDay();
            const hour = now.getHours();
            const minutes = now.getMinutes();
            if (hour === 0 && minutes === 0 && notificationShownToday) {
                setNotificationShownToday(false);
            }
            const isWeekday = day >= 1 && day <= 5;
            const isNotificationTime = hour === 15 && minutes === 0;
            if (isWeekday && isNotificationTime && !notificationShownToday) {
                setShowNotification(true);
                setNotificationShownToday(true);
            }
        };
        const intervalId = setInterval(checkTimeForNotification, 60000);
        return () => clearInterval(intervalId);
    }, [notificationShownToday]);

    useEffect(() => {
        const unsubscribe = onAuthStateChanged(auth, (currentUser) => {
            setUser(currentUser);
            if (!currentUser) {
                setRolElegido(null);
            }
            setIsLoading(false);
        });
        return () => unsubscribe();
    }, []);

    // Padrones completos: solo para personal interno. Las reglas de Firestore
    // rechazan estas lecturas a un productor, que tiene su propia vista.
    useEffect(() => {
        if (isStaffSession) {
            const unsubClientes = onSnapshot(collection(db, "clientes"), (snapshot) => {
                const clientsData = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
                setClientes(clientsData);
            });

            const unsubEspecies = onSnapshot(collection(db, "especies"), (snapshot) => {
                const especiesData = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
                setEspecies(especiesData);
            });

            const unsubCuentasJefferies = onSnapshot(collection(db, "cuentasJefferies"), (snapshot) => {
                const cuentasData = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
                setCuentasJefferies(cuentasData);
            });

            // Suscribirse a títulos A3 desde la colección de saldos
            const unsubSaldos = onSnapshot(collection(db, "saldos"), (snapshot) => {
                const titulosA3Array = [];
                snapshot.docs.forEach(doc => {
                    const data = doc.data();
                    if (data.titulosA3 && Array.isArray(data.titulosA3) && data.titulosA3.length > 0) {
                        data.titulosA3.forEach(titulo => {
                            titulosA3Array.push({
                                nroComitente: data.nroComitente,
                                nombreCliente: data.nombre,
                                codigoEspecie: titulo.codigoEspecie,
                                nombreEspecie: titulo.nombreEspecie,
                                cantidad: titulo.cantidad
                            });
                        });
                    }
                });
                setTitulosA3(titulosA3Array);
            });

            return () => {
                unsubClientes();
                unsubEspecies();
                unsubCuentasJefferies();
                unsubSaldos();
            };
        }
    }, [isStaffSession]);

    const handleSignOut = async () => {
        await signOut(auth);
        setRolElegido(null);
    };

    useEffect(() => {
        if (!isCuentaNoHabilitada) return;
        setLoginError(
            `La cuenta ${user.email} no está habilitada en la Planilla de Operaciones. `
            + 'Si sos productor de axon capital, pedí que te den de alta con este mismo mail '
            + 'en el Sistema de Gestión Comercial.'
        );
        signOut(auth).catch((error) => console.error('No se pudo cerrar la sesión:', error));
    }, [isCuentaNoHabilitada, user]);

    // Al operar como Comercial se registra al usuario en el padrón "comerciales",
    // que alimenta los selects de Comercial de los formularios.
    useEffect(() => {
        if (!isStaffSession || !esComercial) return;
        registerComercialLogin(user).catch((error) =>
            console.error("No se pudo registrar el comercial:", error)
        );
    }, [isStaffSession, esComercial, user]);

    // Registro del ingreso con el rol de la persona (el de base, no el que elige un
    // admin para actuar): le muestra al admin quién entra sin rol asignado.
    const rolRegistrado = rolUsuario?.rol || userRole;
    useEffect(() => {
        if (!isStaffSession || !rolRegistrado) return;
        registrarAccesoUsuario(user, rolRegistrado);
    }, [isStaffSession, rolRegistrado, user]);

    const handleRoleSelect = (role) => {
        const permitidos = esAdmin ? ROLES_OPERATIVOS_ADMIN : ROLES_ELEGIBLES_SIN_ASIGNAR;
        if (permitidos.includes(role)) setRolElegido(role);
    };

    const handleVistaMisClientes = (activo) => {
        setVistaMisClientes(activo);
        try {
            window.localStorage.setItem(VISTA_MIS_CLIENTES_KEY, activo ? 'mis' : 'todos');
        } catch {
            // Sin storage (modo privado): solo se pierde el recuerdo de la elección.
        }
    };

    const handleSaveClient = async (nroComitente, nombre, existingClientId) => {
        if (!nroComitente || !nombre) {
            alert("El número de comitente y el nombre son obligatorios.");
            return;
        }

        try {
            if (existingClientId) {
                const conflicto = clientes.find(
                    cliente => cliente.nroComitente === nroComitente && cliente.id !== existingClientId
                );
                if (conflicto) {
                    alert(`El número de comitente "${nroComitente}" ya está asignado a "${conflicto.nombre}". No se guardaron los cambios.`);
                    return;
                }

                const clientRef = doc(db, "clientes", existingClientId);
                await updateDoc(clientRef, { nroComitente, nombre });
                alert("Cliente actualizado correctamente.");

            } else {
                const clienteExistente = clientes.find(cliente => cliente.nroComitente === nroComitente);

                if (clienteExistente) {
                    alert(`El número de comitente "${nroComitente}" ya está asignado a "${clienteExistente.nombre}". Si querés modificarlo, usá el botón Editar de la lista.`);
                    return;
                }

                // Id determinístico: el chequeo de arriba corre contra el estado en
                // memoria, así que dos altas simultáneas del mismo comitente lo
                // pasaban las dos. Con el comitente como id, la segunda pisa a la
                // primera en vez de crear un duplicado.
                await setDoc(doc(db, "clientes", nroComitente), { nroComitente, nombre }, { merge: true });
                alert("Nuevo cliente agregado correctamente.");
            }
        } catch (error) {
            console.error("Error al guardar el cliente: ", error);
            alert("Hubo un error al guardar el cliente.");
        }
    };
    
    const handleSaveJefferiesAccount = async (accountName, accountNumber, existingId) => {
        if (!accountName || !accountNumber) {
            alert("El nombre y el número de cuenta son obligatorios.");
            return;
        }

        try {
            if (existingId) {
                const conflicto = cuentasJefferies.find(
                    cuenta => cuenta.accountNumber === accountNumber && cuenta.id !== existingId && cuenta.active !== false
                );
                if (conflicto) {
                    alert(`El número de cuenta "${accountNumber}" ya está asignado a "${conflicto.accountName}". No se guardaron los cambios.`);
                    return;
                }

                const accountRef = doc(db, "cuentasJefferies", existingId);
                await updateDoc(accountRef, { accountName, accountNumber });
                alert("Cuenta Jefferies actualizada correctamente.");
            } else {
                const cuentaExistente = cuentasJefferies.find(
                    cuenta => cuenta.accountNumber === accountNumber && cuenta.active !== false
                );

                if (cuentaExistente) {
                    alert(`El número de cuenta "${accountNumber}" ya está asignado a "${cuentaExistente.accountName}". Si querés modificarlo, usá el botón Editar de la lista.`);
                    return;
                }

                await addDoc(collection(db, "cuentasJefferies"), { accountName, accountNumber, active: true });
                alert("Nueva cuenta Jefferies agregada correctamente.");
            }
        } catch (error) {
            console.error("Error al guardar la cuenta Jefferies: ", error);
            alert("Hubo un error al guardar la cuenta Jefferies.");
        }
    };

    const handleSaveEspecie = async (ticker, moneda) => {
        try {
            const especieRef = doc(db, "especies", ticker);
            await setDoc(especieRef, { moneda: moneda });
            alert(`Especie "${ticker}" guardada correctamente con la moneda "${moneda}".`);
        } catch (error) {
            console.error("Error al guardar la especie: ", error);
            alert("Hubo un error al guardar la especie.");
        }
    };
    
    if (isLoading || (user && isProductorLoading) || (isStaffSession && isRolLoading)) {
        return <SplashScreen />;
    }

    // Una cuenta externa fuera del padrón ve el login con el aviso mientras se
    // cierra su sesión: nunca llega a la selección de rol del personal interno.
    if (!user || isCuentaNoHabilitada) {
        return <LoginScreen setLoginError={setLoginError} loginError={loginError} />;
    }

    if (productor) {
        if (productor.active === false) {
            return <ProductorInactivoScreen productor={productor} onSignOut={handleSignOut} />;
        }
        return (
            <ThemeProvider theme={theme}>
                <ProductorView
                    user={user}
                    productor={productor}
                    onSignOut={handleSignOut}
                    updateAvailable={updateAvailable}
                    onReloadNow={reloadNow}
                />
            </ThemeProvider>
        );
    }

    if (user && !userRole) {
        return <RoleSelectionModal onRoleSelect={handleRoleSelect} onSignOut={handleSignOut} />;
    }

    const clientesDb = clientes.reduce((acc, client) => {
        acc[client.nroComitente] = client.nombre;
        return acc;
    }, {});

    // Datos fiscales ya conocidos de cada comitente (CUIT y tipo de documento),
    // para autocompletar la carga de órdenes de licitaciones.
    const datosFiscalesDb = clientes.reduce((acc, client) => {
        if (client.cuit || client.tipoDocumento) {
            acc[client.nroComitente] = {
                cuit: client.cuit || '',
                tipoDocumento: client.tipoDocumento || '',
            };
        }
        return acc;
    }, {});

    // Índice inverso CUIT -> comitente. Lo necesita la importación de cauciones
    // de A3, que identifica al cliente por CUIT y no por número de comitente.
    // Se indexa por dígitos para no depender del formato con que se guardó.
    const comitentePorCuit = clientes.reduce((acc, client) => {
        const digitos = parseCuitDigits(client.cuit);
        if (digitos.length === 11 && client.nroComitente) {
            acc[digitos] = client.nroComitente;
        }
        return acc;
    }, {});

    const cuentasJefferiesDb = cuentasJefferies.reduce((acc, cuenta) => {
        if (cuenta.active === false) return acc;
        acc[cuenta.accountNumber] = cuenta.accountName;
        return acc;
    }, {});


    return (
        <ThemeProvider theme={theme}>
          <SaldosClientesProvider user={user} clientesDb={clientesDb}>
            <AppWrapper>
                {updateAvailable && <UpdateBanner onReloadNow={reloadNow} />}
                <Header
                    user={user}
                    userRole={userRole}
                    onAddClientClick={() => setIsClientModalOpen(true)}
                    onAddEspecieClick={() => setIsAddEspecieModalOpen(true)}
                    onProductoresClick={esComercial ? undefined : () => setIsProductoresModalOpen(true)}
                    onUsuariosClick={puedeVerUsuarios ? () => setIsUsuariosModalOpen(true) : undefined}
                    onSignOut={handleSignOut}
                    onCierreDiarioClick={esComercial ? undefined : () => setIsCierreModalOpen(true)}
                    rolesOperativos={esAdmin ? ROLES_OPERATIVOS_ADMIN : null}
                    onRolOperativoChange={handleRoleSelect}
                />
                <MainContent>
                    <MainTabBar tabs={tabsVisibles} activeTab={tabActiva} onChange={setActiveTab} />

                    {esComercial && TABS_CON_FILTRO_MIS_CLIENTES.includes(tabActiva) && (
                        <MisClientesToggle
                            activo={vistaMisClientes}
                            onChange={handleVistaMisClientes}
                            cantidadCuentas={carteraComercial.comitentes.length}
                        />
                    )}

                    {tabActiva === 'misClientes' &&
                        <MisClientesTab
                            cartera={carteraComercial}
                            onPrecargarCaucion={(data) => setCaucionModal({ open: true, prefill: data })}
                            avisoSinCuentas="Tus clientes se asignan en el Sistema de Gestión Comercial: cuando te los asignen aparecen acá en la sincronización siguiente (cada hora, en días hábiles)."
                        />
                    }
                    {tabActiva === 'dashboard' &&
                        <DashboardTab
                            onPrecargarCaucion={(data) => setCaucionModal({ open: true, prefill: data })}
                            onNavigateToSaldos={() => setActiveTab('saldos')}
                            alcance={alcanceDashboard}
                            mostrarVencimientos={esComercial}
                        />
                    }
                    {tabActiva === 'cauciones' &&
                        <CaucionesTab
                            userRole={userRole}
                            userName={user.displayName}
                            userId={user.uid}
                            clientesDb={clientesDb}
                            comitentePorCuit={comitentePorCuit}
                            especies={especies}
                            onViewComment={handleViewComment}
                            onNavigateToSaldos={() => setActiveTab('saldos')}
                            filtroMisClientes={filtroMisClientes}
                        />
                    }
                    {tabActiva === 'operaciones' &&
                        <OperacionesTab
                            userRole={userRole}
                            userName={user.displayName}
                            userId={user.uid}
                            clientesDb={clientesDb}
                            cuentasJefferiesDb={cuentasJefferiesDb}
                            especies={especies}
                            titulosA3={titulosA3}
                            onViewComment={handleViewComment}
                            filtroMisClientes={filtroMisClientes}
                        />
                    }
                    {tabActiva === 'instrucciones' &&
                        <InstruccionesTab
                            userRole={userRole}
                            userName={user.displayName}
                            userId={user.uid}
                            clientesDb={clientesDb}
                            especies={especies}
                            onViewComment={handleViewComment}
                            filtroMisClientes={filtroMisClientes}
                        />
                    }
                    {tabActiva === 'licitaciones' &&
                        <LicitacionesTab
                            userRole={userRole}
                            userName={user.displayName}
                            userId={user.uid}
                            clientesDb={clientesDb}
                            datosFiscalesDb={datosFiscalesDb}
                            onViewComment={handleViewComment}
                            filtroMisClientes={filtroMisClientes}
                        />
                    }
                    {tabActiva === 'saldos' &&
                        <ControlSaldosTab
                            clientesDb={clientesDb}
                            especies={especies}
                            onPrecargarCaucion={(data) => setCaucionModal({ open: true, prefill: data })}
                            filtroMisClientes={filtroMisClientes}
                            soloLectura={esComercial}
                        />
                    }
                    {tabActiva === 'senebi' && 
                        <SenebiTab 
                            userRole={userRole} 
                            userName={user.displayName} 
                            especies={especies}
                            onViewComment={handleViewComment}
                        />
                    }
                </MainContent>

                {/* Modal de carga de cauciones compartido: se abre como overlay desde el Dashboard
                    o Control de Saldos sin cambiar de pestaña, para poder seguir cargando. */}
                <CaucionFormModal
                    isOpen={caucionModal.open}
                    onClose={() => setCaucionModal({ open: false, prefill: null })}
                    clientesDb={clientesDb}
                    userRole={userRole}
                    userName={user.displayName}
                    userId={user.uid}
                    editingCaucion={null}
                    prefill={caucionModal.prefill}
                />

                <ClientManagementModal
                    isOpen={isClientModalOpen}
                    onClose={() => setIsClientModalOpen(false)}
                    clientes={clientes}
                    cuentasJefferies={cuentasJefferies}
                    onSaveClient={handleSaveClient}
                    onSaveJefferiesAccount={handleSaveJefferiesAccount}
                />
                <AddEspecieModal
                    isOpen={isAddEspecieModalOpen}
                    onClose={() => setIsAddEspecieModalOpen(false)}
                    onSaveEspecie={handleSaveEspecie}
                    especies={especies}
                />
                <ProductoresAdminModal
                    isOpen={isProductoresModalOpen}
                    onClose={() => setIsProductoresModalOpen(false)}
                    user={user}
                />
                {puedeVerUsuarios && (
                    <UsuariosAdminModal
                        isOpen={isUsuariosModalOpen}
                        onClose={() => setIsUsuariosModalOpen(false)}
                        user={user}
                        rolUsuario={rolUsuario}
                    />
                )}
                {showNotification && (
                    <Notification
                        message="Recordatorio: Realizar el control de saldos de clientes."
                        onClose={() => setShowNotification(false)}
                    />
                )}
                <CierreDiarioModal
                    isOpen={isCierreModalOpen}
                    onClose={() => setIsCierreModalOpen(false)}
                    onExport={handleExportToDrive}
                    onExecute={handleExecuteCierre}
                    isExporting={isExportingToDrive}
                    isExecuting={isExecutingCierre}
                />
                <CommentModal
                    isOpen={commentModalData.isOpen}
                    onClose={handleCloseCommentModal}
                    title={commentModalData.title}
                    content={commentModalData.content}
                />
                
                {/* Modal de recordatorio de exportación (18:00 hs). No va para el comercial:
                    la exportación incluye SENEBI, que no puede leer. */}
                <ExportReminderModal
                    isOpen={showReminder && !!user && !esComercial}
                    onExport={handleExportFromReminder}
                    onDismiss={dismissReminder}
                    currentTime={currentTime}
                />
            </AppWrapper>
          </SaldosClientesProvider>
        </ThemeProvider>
    );
}
